import React, { useEffect, useRef, useState } from "react";

/** Common Components */
import Buttons from "../../../../components/ui/button/Buttons";
import FormCreator from "../../../../components/forms/FormCreator";

/** Configuration Files */
import { addEditXMLReportsFormConfig } from "./addEditXMLReport.formfields.config";
import {
  useAddXMLReportMutation,
  useLazyGetAllXmlTemplateListQuery,
  useUpdateXMLReportMutation,
} from "../../../../app/services/xmlReportAPI";
import ToastService from "../../../../services/toastService/ToastService";
import { mapDropdownOptions } from "../../../../components/forms/libs/data/mapDropdownOptions";
import {
  useLazyGetAllReportTemplateListQuery,
} from "../../../../app/services/reportTemplateAPI";
import CenterModal from "../../../../components/ui/centerModal/CenterModal";
import ViewSchemaFieldModel from "./ViewSchemaFieldModel";
import "../../XMLReports.scss";
import MappingFieldPrimeList from "./MappingFieldPrimeList";
import PropTypes from "prop-types";

const AddEditXMLReports = (props) => {
  const formRef = useRef(null);
  const [xMLReportsForm, setXSDTemplateListForm] = useState(
    addEditXMLReportsFormConfig
  );
  const [isXSDTemplateModelOpen, setIsXSDTemplateModelOpen] = useState(false);
  const [isOpenPrimData,setIsOpenPrimData]=useState(false)

  // API Call
  const [
    addXMLReport,
    {
      isLoading: isXMLReportAdding,
      isSuccess: isXMLReportAdded,
      data: XMLReportData,
    },
  ] = useAddXMLReportMutation();

  const [
    updateXMLReport,
    {
      isLoading: isXMLReportUpdating,
      isSuccess: isXMLReportUpdated,
      data: XMLReportUpdatedData,
    },
  ] = useUpdateXMLReportMutation();

  const [
    getAllXmlTemplateList,
    {
      isLoading: isAllXmlTemplateListLoading,
      isSuccess: isAllXmlTemplateListSuccess,
      data: AllXmlTemplateListData,
    },
  ] = useLazyGetAllXmlTemplateListQuery();

  const [
    getAllReportTemplateList,
    {
      isLoading: isAllReportTemplateLoading,
      isSuccess: isAllReportTemplateSuccess,
      data: AllReportTemplateData,
    },
  ] = useLazyGetAllReportTemplateListQuery();

  useEffect(() => {
    getAllXmlTemplateList();
    getAllReportTemplateList();
  }, []);

  useEffect(() => {
    if (
      !isAllReportTemplateLoading &&
      isAllReportTemplateSuccess &&
      AllReportTemplateData
    ) {
      const form = { ...xMLReportsForm };
      mapDropdownOptions(
        addEditXMLReportsFormConfig,
        "basicDetailSection",
        "reportTemplateID",
        AllReportTemplateData,
        "reportTemplateID",
        "reportTemplateName"
      );
      setXSDTemplateListForm(form);
    }
  }, [
    isAllReportTemplateLoading,
    isAllReportTemplateSuccess,
    AllReportTemplateData,
  ]);

  useEffect(() => {
    if (
      !isAllXmlTemplateListLoading &&
      isAllXmlTemplateListSuccess &&
      AllXmlTemplateListData
    ) {
      const form = { ...xMLReportsForm };
      mapDropdownOptions(
        addEditXMLReportsFormConfig,
        "basicDetailSection",
        "xmlTemplateId",
        AllXmlTemplateListData,
        "xmlReportTemplateId",
        "xmlReportTemplateName"
      );
      setXSDTemplateListForm(form);
    }
  }, [
    isAllXmlTemplateListLoading,
    isAllXmlTemplateListSuccess,
    AllXmlTemplateListData,
  ]);

  const resetForm = () => {
    const form = { ...addEditXMLReportsFormConfig };
    form.initialState = { ...props.initData };
    setXSDTemplateListForm(form);
  };

  const handleCloseClick = () => {
    resetForm();
    props?.onClose();
  };
  const handleMappingFieldClick = () => {
    setIsXSDTemplateModelOpen(true);
  };
  const handlePrimeClick = () => {
    setIsOpenPrimData(true);
  };
  const handleCloseModel = () => {
    setIsXSDTemplateModelOpen(false);
    setIsOpenPrimData(false)
  };
  const handleAddUpdateClick = () => {
    const formData = formRef.current.getFormData();
    if (formData) {
      const req = {
        xmlReportId: formData.xmlReportId,
        xmlReportName: formData.xmlReportName,
        status: formData.status === true ? 1 : 0,
        description: formData.description,
        reportTemplateID:
          formData.reportTemplateID &&
          typeof formData.reportTemplateID === "object"
            ? formData.reportTemplateID.value
            : formData.reportTemplateID,
        xmlTemplateId:
          formData.xmlTemplateId && typeof formData.xmlTemplateId === "object"
            ? formData.xmlTemplateId.value
            : formData.xmlTemplateId,
      };
      if (props.isEdit) {
        req.xmlReportId = formData.xmlReportId;
        updateXMLReport(req);
      } else {
        addXMLReport(req);
      }
    }
  };

  useEffect(() => {
    if (isXMLReportAdded && XMLReportData) {
      if (XMLReportData?.errorMessage?.includes("exist")) {
        ToastService.warning(XMLReportData.errorMessage);
      } else {
        ToastService.success(XMLReportData.errorMessage);
        props.onSuccess?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isXMLReportAdded, XMLReportData]);

  useEffect(() => {
    if (isXMLReportUpdated && XMLReportUpdatedData) {
      const msg = XMLReportUpdatedData?.errorMessage ?? "";
      if (msg.includes("NO RECORD FOUND") || msg.includes("exist")) {
        ToastService.warning(msg);
      } else {
        ToastService.success(msg);
        props.onSuccess?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isXMLReportUpdated, XMLReportUpdatedData]);

  useEffect(() => {
    if (props.isEdit && props.initData) {
      const form = { ...xMLReportsForm };
      form.initialState = {
        ...props.initData,
        xmlReportId: props.initData.xmlReportId,
        xmlReportName: props.initData.xmlReportName,
        xmlTemplateId: props.initData.xmlTemplateId,
        status: props.initData.status === 1 ? true : false,
        reportTemplateID: props.initData.reportTemplateID,
      };
      setXSDTemplateListForm(form);
    } else {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isEdit, props.initData]);

  return (
    <>
      <div>
        <FormCreator ref={formRef} config={xMLReportsForm} />
      </div>
      <div className="form-button-group">
        <Buttons
          buttonText="Cancel"
          buttonTypeClassName="btn secondary-btn"
          onClick={handleCloseClick}
        />
        <Buttons
          buttonText={props.isEdit ? "Update" : "Add"}
          buttonTypeClassName="btn blue-btn w-full"
          onClick={handleAddUpdateClick}
          isLoading={isXMLReportAdding || isXMLReportUpdating}
        />
          <Buttons
           buttonText={`Mapping Fields`}
          buttonTypeClassName="btn info-btn w-full"
          onClick={handlePrimeClick}
          />
      </div>

      <CenterModal
        modalTitle={`View Schema Field`}
        modalSize={"w-55"}
        isOpen={isXSDTemplateModelOpen}
        onClose={handleCloseModel}
        transition={"grow"}
        className={"modal-grid-scroll"}
      >
        <div className="view-schema-filed-sec schema-table schema-table-model">
          <ViewSchemaFieldModel
            reportTemplateID={props?.initData?.reportTemplateID}
            xmlTemplateId={props?.initData?.xmlTemplateId}
            xmlReportId={props?.initData?.xmlReportId}
          />
        </div>
      </CenterModal>
      <CenterModal
        modalTitle={`View Schema Field`}
        modalSize={"w-55"}
        isOpen={isOpenPrimData}
        onClose={handleCloseModel}
        transition={"grow"}
        className={"modal-grid-scroll"}
      >
        <div className="view-schema-filed-sec schema-table schema-table-model">
          < MappingFieldPrimeList
            reportTemplateID={props?.initData?.reportTemplateID}
            xmlTemplateId={props?.initData?.xmlTemplateId}
            xmlReportId={props?.initData?.xmlReportId}
          />
        </div>
      </CenterModal>
    </>
  );
};

AddEditXMLReports.prototype = {
  isEdit: PropTypes.bool, 
  initData: PropTypes.shape({
    xmlReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    xmlReportName: PropTypes.string,
    xmlTemplateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.bool,
    xmlReportId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }), 
  onClose: PropTypes.func, 
  onSuccess: PropTypes.func,
}

export default AddEditXMLReports;
